import apiRoutes from '~/const/apiRoutes';
import type { AccountDeletionPreview } from '~/types/AccountDeletionPreview';
import type { AdConsent } from '~/types/AdConsent';
import type { AdConsentResult } from '~/composables/useAdConsent';

export const useClientAccountSettingsStore = defineStore(
   'clientAccountSettingsStore',
   () => {
      const { clientInformation, clientProfile } = storeToRefs(useClientInfo());
      const { setSnackbarDetail } = useSnackbarStore();
      const changePassword = async (passwordData: {
         current_password: string;
         new_password: string;
         confirm_password: string;
      }) => {
         const { data, error } = await useIFetch(
            apiRoutes?.client?.account.changePassword,
            {
               method: 'POST',
               body: passwordData,
            }
         );
         if (error.value)
            setSnackbarDetail({
               show: true,
               title: error.value.data.message || 'Something went wrong',
               type: 'error',
            });
         if (data.value)
            setSnackbarDetail({
               show: true,
               title: 'Your new password is set successfully',
               type: 'message',
            });
         return { data, error };
      };
      const changeEmail = async (email: string) => {
         const { data, error } = await useIFetch(
            apiRoutes?.client?.account.changeEmail,
            {
               method: 'POST',
               body: {
                  email,
               },
            }
         );
         if (error.value)
            setSnackbarDetail({
               show: true,
               title: error.value.data.message || 'Something went wrong',
               type: 'error',
            });
         if (data.value)
            setSnackbarDetail({
               show: true,
               title: 'Your new email is set successfully',
               type: 'message',
            });
         return { data, error };
      };
      const verifiedEmail = async (email: string, code: string) => {
         let { data, error } = await useIFetch<{ message: string }>(
            apiRoutes?.client?.auth.verifyToken,
            {
               method: 'POST',
               body: {
                  action: 'change_email',
                  email: email,
                  token: code,
               },
            }
         );
         if (data.value) {
            setSnackbarDetail({
               show: true,
               title: data.value.message,
               type: 'message',
            });
            if (clientInformation.value?.client && clientProfile.value) {
               clientInformation.value.client.email = email;
               clientProfile.value.email = email;
            }
         }
         if (error?.value)
            setSnackbarDetail({
               show: true,
               title: error.value.data.message,
               type: 'error',
            });
         return { data, error };
      };
      const resendVerificationCode = async (email: string) => {
         let { data, error } = await useIFetch<{ message: string }>(
            apiRoutes?.client?.auth.resendVerifyToken,
            {
               method: 'POST',
               body: {
                  action: 'change_email',
                  email: email,
               },
            }
         );
         if (data.value)
            setSnackbarDetail({
               show: true,
               title: data.value.message,
               type: 'message',
            });

         if (error.value)
            setSnackbarDetail({
               show: true,
               title: error.value.data.message,
               type: 'error',
            });
         return { data, error };
      };
      // Read-only summary of what the deletion cascade will touch. Called when the
      // user opens the Delete account dialog so the consent screen can show the real
      // numbers (credits, subscription, bookings, etc.) before they commit.
      //
      // Intentionally does NOT call setSnackbarDetail on error — the caller renders
      // the error inline inside the dialog. Toasting here would double-surface it.
      const previewAccountDeletion = async () => {
         const { data, error } = await useIFetch<AccountDeletionPreview>(
            apiRoutes?.client?.account.deletePreview,
            { method: 'GET' }
         );
         return { data, error };
      };

      // Step 1 of the self-service delete flow: emails the user a confirmation link
      // + 5-digit OTP via the shared setVerifyToken infrastructure. No state change
      // happens server-side at this point.
      const requestAccountDeletion = async () => {
         const { data, error } = await useIFetch<{ message: string }>(
            apiRoutes?.client?.account.deleteRequest,
            {
               method: 'POST',
            }
         );
         if (error.value)
            setSnackbarDetail({
               show: true,
               title: error.value.data?.message || 'Failed to send confirmation email',
               type: 'error',
            });
         return { data, error };
      };

      // Step 2 of the self-service delete flow: validates the OTP/link token and
      // enqueues the cascade. After this call returns 200 the client account is
      // already locked (status flipped, tokens revoked) — the cascade runs async.
      //
      // Uses $fetch directly (NOT useIFetch) for two reasons:
      //   1. The endpoint is intentionally unauthenticated — the token IS the credential.
      //      useIFetch always injects the access token; backend ignores it but it's noise.
      //   2. useIFetch globally intercepts 401 and triggers a refresh-then-redirect to
      //      /auth/client/login. For this endpoint, 401 means "link expired or already
      //      used" — we want to surface that as the resend UX, not force-logout.
      // The caller (component or deep-link page) is responsible for displaying any error.
      const confirmAccountDeletion = async (email: string, token: string) => {
         const config = useRuntimeConfig();
         try {
            const result = await $fetch<{ message: string }>(
               apiRoutes?.client?.account.deleteConfirm,
               {
                  method: 'POST',
                  baseURL: config.public.API_BASE_URL,
                  body: { token, email },
               }
            );
            return { data: result, error: null as null | { statusCode: number; message: string } };
         } catch (e: unknown) {
            const err = e as {
               statusCode?: number;
               status?: number;
               data?: { message?: string };
            };
            const statusCode = err.statusCode ?? err.status ?? 500;
            const message = err.data?.message ?? '';
            return { data: null as null | { message: string }, error: { statusCode, message } };
         }
      };

      // Resend wraps the shared OTP resend with action=delete_account so the
      // "Didn't get the email?" link in the dialog reuses our existing throttle
      // and dedup behavior. The caller is responsible for surfacing errors inline
      // (the dialog already renders otpErrorMessage), so we deliberately don't toast
      // here to avoid double-surfacing the same failure.
      const resendDeletionToken = async (email: string) => {
         return await useIFetch<{ message: string }>(
            apiRoutes?.client?.auth.resendVerifyToken,
            {
               method: 'POST',
               body: { action: 'delete_account', email },
            }
         );
      };

      // Reads and writes the advertising-measurement choice. Neither toasts: the
      // switch shows its own inline error and reverts, so a toast would double up.
      // The GET and PUT share a URL, so each needs its own key or they share one
      // asyncData entry and cancel each other.
      const toAdConsentResult = (
         data: AdConsent | null,
         error: { statusCode?: number } | null
      ): AdConsentResult => ({
         consent: data ?? null,
         error: error ? { status: error.statusCode } : null,
      });

      // A save that never answers must end as a failure, or the popup would sit
      // locked with no way out.
      const AD_CONSENT_SAVE_TIMEOUT_MS = 15000;

      // The popup passes its own keys so a request from the settings page can
      // never share (and cancel) its asyncData entry.
      const getAdConsent = async (
         key = 'client-ad-consent-get'
      ): Promise<AdConsentResult> => {
         const { data, error } = await useIFetch<AdConsent>(
            apiRoutes?.client?.privacy.adConsent,
            { method: 'GET', key }
         );
         return toAdConsentResult(data.value, error.value);
      };

      const setAdConsent = async (
         granted: boolean,
         key = 'client-ad-consent-put'
      ): Promise<AdConsentResult> => {
         const { data, error } = await useIFetch<AdConsent>(
            apiRoutes?.client?.privacy.adConsent,
            { method: 'PUT', body: { granted }, key, timeout: AD_CONSENT_SAVE_TIMEOUT_MS }
         );
         return toAdConsentResult(data.value, error.value);
      };

      return {
         changePassword,
         changeEmail,
         verifiedEmail,
         resendVerificationCode,
         previewAccountDeletion,
         requestAccountDeletion,
         confirmAccountDeletion,
         resendDeletionToken,
         getAdConsent,
         setAdConsent,
      };
   }
);
